import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, MessageSquare, Power } from "lucide-react";
import { authFetch } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import AdminLoading from "../../components/admin/AdminLoading";
import AdminEmptyState from "../../components/admin/AdminEmptyState";
import AdminTable, { AdminMobileCard, adminTableHeadClass, adminTableRowClass, adminClickableRowClass } from "../../components/admin/AdminTable";
import ConfirmDialog from "../../components/admin/ConfirmDialog";
import { toast } from "sonner";
import {
  ADMIN_BADGE_OK,
  ADMIN_BADGE_OFF,
  ADMIN_FIELD,
  ADMIN_LABEL,
  ADMIN_PANEL,
  BTN_OUTLINE,
  BTN_PRIMARY,
} from "../../config/brand";

const emptyForm = {
  name: "",
  role: "",
  content: "",
  rating: 5,
  image_url: "",
  display_order: 0,
  active: true,
};

export default function AdminTestimonials() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await authFetch("/api/admin/testimonials");
      if (res.ok) setTestimonials(await res.json());
      else toast.error("Impossible de charger les avis");
    } catch {
      toast.error("Erreur réseau");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setErrors({});
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      name: item.name || "",
      role: item.role || "",
      content: item.content || "",
      rating: item.rating || 5,
      image_url: item.image_url || "",
      display_order: item.display_order ?? 0,
      active: item.active !== false,
    });
    setErrors({});
    setDialogOpen(true);
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Le nom est requis";
    if (!form.content.trim()) next.content = "Le commentaire est requis";
    if (form.rating && (Number(form.rating) < 1 || Number(form.rating) > 5)) {
      next.rating = "La note doit être entre 1 et 5";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const save = async () => {
    if (!validate()) return;

    setSaving(true);
    const payload = {
      name: form.name.trim(),
      role: form.role.trim(),
      content: form.content.trim(),
      rating: Number(form.rating) || 5,
      image_url: form.image_url.trim(),
      display_order: Number(form.display_order) || 0,
      active: form.active !== false,
    };
    try {
      const url = editing
        ? `/api/admin/testimonials/${editing.id}`
        : "/api/admin/testimonials";
      const method = editing ? "PUT" : "POST";
      const res = await authFetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Erreur");
      }
      toast.success(editing ? "Avis mis à jour" : "Avis créé");
      setDialogOpen(false);
      load();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (item) => {
    setTogglingId(item.id);
    const nextActive = item.active === false;
    try {
      const res = await authFetch(`/api/admin/testimonials/${item.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ active: nextActive }),
      });
      if (!res.ok) throw new Error("Erreur");
      toast.success(nextActive ? "Avis activé" : "Avis désactivé — retiré du site");
      load();
    } catch {
      toast.error("Impossible de changer le statut");
    } finally {
      setTogglingId(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await authFetch(`/api/admin/testimonials/${deleteTarget.id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Erreur");
      toast.success(`Avis de ${deleteTarget.name} supprimé`);
      setDeleteTarget(null);
      load();
    } catch {
      toast.error("Impossible de supprimer");
    } finally {
      setDeleting(false);
    }
  };

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  return (
    <div>
      <AdminPageHeader
        icon={MessageSquare}
        title="Avis clients"
        description="Gérez les témoignages affichés sur l'accueil"
        action={
          <Button onClick={openCreate} className={BTN_PRIMARY}>
            <Plus className="h-4 w-4 mr-2" />
            Nouvel avis
          </Button>
        }
      />

      {loading ? (
        <AdminLoading message="Chargement des avis..." />
      ) : testimonials.length === 0 ? (
        <AdminEmptyState
          icon={MessageSquare}
          title="Aucun avis"
          description="Ajoutez votre premier témoignage client pour l'afficher sur la page d'accueil."
          action={
            <Button onClick={openCreate} className={BTN_PRIMARY}>
              <Plus className="h-4 w-4 mr-2" />
              Nouvel avis
            </Button>
          }
        />
      ) : (
        <AdminTable
          mobileCards={testimonials.map((item) => (
            <AdminMobileCard
              key={item.id}
              title={item.name}
              subtitle={item.role}
              onClick={() => openEdit(item)}
              badge={
                <Badge className={item.active !== false ? ADMIN_BADGE_OK : ADMIN_BADGE_OFF}>
                  {item.active !== false ? "Actif" : "Inactif"}
                </Badge>
              }
              meta={[
                { label: "Note", value: `${item.rating}/5` },
                { label: "Commentaire", value: item.content },
              ]}
              actions={
                <>
                  <Button variant="ghost" size="sm" onClick={() => openEdit(item)} aria-label="Modifier">
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleActive(item)}
                    disabled={togglingId === item.id}
                    className={item.active === false ? "text-emerald-600" : "text-amber-600"}
                  >
                    <Power className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(item)} aria-label={`Supprimer l'avis de ${item.name}`}>
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </Button>
                </>
              }
            />
          ))}
        >
          <Table>
            <TableHeader>
              <TableRow className={`${adminTableRowClass} hover:bg-transparent`}>
                <TableHead className={adminTableHeadClass}>Auteur</TableHead>
                <TableHead className={adminTableHeadClass}>Note</TableHead>
                <TableHead className={adminTableHeadClass}>Commentaire</TableHead>
                <TableHead className={adminTableHeadClass}>Ordre</TableHead>
                <TableHead className={adminTableHeadClass}>Statut</TableHead>
                <TableHead className={`${adminTableHeadClass} text-right`}>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {testimonials.map((item) => (
                <TableRow key={item.id} className={adminClickableRowClass} onClick={() => openEdit(item)}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {item.image_url && (
                        <img src={item.image_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                      )}
                      <div>
                        <p className="text-slate-900 font-medium">{item.name}</p>
                        <p className="text-slate-500 text-xs">{item.role}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-yellow-500 font-medium">{item.rating}/5</span>
                  </TableCell>
                  <TableCell className="text-slate-400 max-w-xs truncate">{item.content}</TableCell>
                  <TableCell className="text-slate-400 tabular-nums">{item.display_order}</TableCell>
                  <TableCell>
                    <Badge className={item.active !== false ? ADMIN_BADGE_OK : ADMIN_BADGE_OFF}>
                      {item.active !== false ? "Actif" : "Inactif"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(item)} aria-label="Modifier">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleActive(item)}
                      disabled={togglingId === item.id}
                      aria-label={item.active === false ? "Activer" : "Désactiver"}
                      className={item.active === false ? "text-emerald-600" : "text-amber-600"}
                    >
                      {togglingId === item.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Power className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteTarget(item)}
                      aria-label={`Supprimer l'avis de ${item.name}`}
                    >
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </AdminTable>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className={`${ADMIN_PANEL} text-slate-900 max-w-lg`}>
          <DialogHeader>
            <DialogTitle>{editing ? "Modifier l'avis" : "Nouvel avis"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className={ADMIN_LABEL}>Nom *</Label>
              <Input
                value={form.name}
                onChange={(e) => updateField("name", e.target.value)}
                className={`${ADMIN_FIELD} ${errors.name ? "border-red-500" : ""}`}
              />
              {errors.name && <p className="text-red-600 text-xs mt-1">{errors.name}</p>}
            </div>
            <div>
              <Label className={ADMIN_LABEL}>Rôle / contexte</Label>
              <Input
                value={form.role}
                onChange={(e) => updateField("role", e.target.value)}
                placeholder="Maman de Lucas, 9 ans"
                className={ADMIN_FIELD}
              />
            </div>
            <div>
              <Label className={ADMIN_LABEL}>Commentaire *</Label>
              <Textarea
                value={form.content}
                onChange={(e) => updateField("content", e.target.value)}
                className={`${ADMIN_FIELD} ${errors.content ? "border-red-500" : ""}`}
                rows={4}
              />
              {errors.content && <p className="text-red-600 text-xs mt-1">{errors.content}</p>}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className={ADMIN_LABEL}>Note (1-5)</Label>
                <Input
                  type="number"
                  min={1}
                  max={5}
                  value={form.rating}
                  onChange={(e) => updateField("rating", e.target.value)}
                  className={`${ADMIN_FIELD} ${errors.rating ? "border-red-500" : ""}`}
                />
                {errors.rating && <p className="text-red-600 text-xs mt-1">{errors.rating}</p>}
              </div>
              <div>
                <Label className={ADMIN_LABEL}>Ordre d'affichage</Label>
                <Input
                  type="number"
                  value={form.display_order}
                  onChange={(e) => updateField("display_order", e.target.value)}
                  className={ADMIN_FIELD}
                />
              </div>
            </div>
            <div>
              <Label className={ADMIN_LABEL}>Image / avatar (URL)</Label>
              <Input
                value={form.image_url}
                onChange={(e) => updateField("image_url", e.target.value)}
                placeholder="https://..."
                className={ADMIN_FIELD}
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={form.active !== false}
                onChange={(e) => updateField("active", e.target.checked)}
                className="rounded border-slate-600"
              />
              Afficher sur le site (actif)
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className={BTN_OUTLINE} disabled={saving}>
              Annuler
            </Button>
            <Button onClick={save} disabled={saving} className={BTN_PRIMARY}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Enregistrement...
                </>
              ) : (
                "Enregistrer"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && !deleting && setDeleteTarget(null)}
        title={`Supprimer l'avis de ${deleteTarget?.name || "ce client"} ?`}
        description={`L'avis de ${deleteTarget?.name || "ce client"} sera définitivement supprimé.`}
        confirmLabel="Supprimer"
        onConfirm={confirmDelete}
        confirming={deleting}
      />
    </div>
  );
}
